import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL || '';

function useApi() {
  return useMemo(() => {
    const token = localStorage.getItem('session_token') || '';
    const h = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(`${API}${url}`, { headers: h }).then(r => r.json()),
      post: (url, body) => fetch(`${API}${url}`, { method: 'POST', headers: h, body: JSON.stringify(body) }).then(r => r.json()),
      put: (url, body) => fetch(`${API}${url}`, { method: 'PUT', headers: h, body: JSON.stringify(body) }).then(r => r.json()),
      delete: (url) => fetch(`${API}${url}`, { method: 'DELETE', headers: h }).then(r => r.json()),
    };
  }, []);
}

// Hardware model images (SVG-based visual representations)
function OLTHardwareImage({ model, vendor, cards = [], uplinks = [], loading = false, notes = {} }) {
  const ponCards = cards.filter(c => /gpon|pon/i.test(`${c.role || ''} ${c.card_type || ''} ${c.real_type || ''}`));
  const activeCards = cards.filter(c => /normal|online|active/i.test(c.status || ''));
  const linkUp = uplinks.filter(p => /up|active|connected/i.test(p.status || p.oper_status || ''));
  const unknownLinks = uplinks.filter(p => !p.status && !p.oper_status);
  const inventoryNotes = [
    !cards.length && (notes.cards || (!loading ? 'No cached card inventory. Refresh from the OLT cards tab.' : '')),
    !uplinks.length && (notes.uplinks || (!loading ? 'No cached uplink inventory. Refresh from the Uplink tab.' : '')),
  ].filter(Boolean);
  const cardName = c => c.real_type || c.card_type || c.role || 'Card';
  return (
    <div style={{ background: '#1a1a2e', borderRadius: 8, padding: 16, border: '1px solid #333' }}>
      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Hardware reported by OLT</div>
      <div style={{ background: '#24243b', borderRadius: 6, padding: 12, border: '1px solid #41415a' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{vendor || 'OLT'} {model || 'Unknown model'}</span>
          <span style={{ color: loading ? '#fbbf24' : '#9ca3af', fontSize: 10 }}>{loading ? 'Reading…' : cards.length ? `${activeCards.length}/${cards.length} cards normal` : 'No card data'}</span>
        </div>
        <div style={{ fontSize: 10, color: '#aaa', marginBottom: 5 }}>INSTALLED PON CARDS {ponCards.length ? `· ${ponCards.reduce((n,c) => n + (Number(c.ports) || 0), 0)} reported ports` : ''}</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
          {ponCards.length ? ponCards.map(c => <div key={c.slot} title={`Slot ${c.slot}: ${c.status || 'status not reported'}`} style={{ background: /normal|online|active/i.test(c.status || '') ? '#087f5b' : '#745b27', color: '#fff', borderRadius: 4, padding: '6px 8px', fontSize: 10 }}>{`Slot ${c.slot} · ${cardName(c)} · ${c.ports || 0} PON`}</div>) : <span style={{ color: '#9ca3af', fontSize: 11 }}>{loading ? 'Loading installed cards…' : 'No PON card data reported'}</span>}
        </div>
        <div style={{ fontSize: 10, color: '#aaa', marginBottom: 5 }}>UPLINK INTERFACES {uplinks.length ? `· ${linkUp.length} up${unknownLinks.length ? ` · ${unknownLinks.length} status unavailable` : ''}` : ''}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {uplinks.length ? uplinks.slice(0, 12).map((p, i) => {
            const name = p.port_name || p.name || p.if_name || `Uplink ${i + 1}`;
            const status = p.status || p.oper_status || 'Status unavailable';
            const up = /up|active|connected/i.test(status);
            return <div key={p.id || name} title={`${name}: ${status}${p.speed ? ` · ${p.speed}` : ''}`} style={{ background: up ? '#087fbb' : '#4b5563', color: '#fff', borderRadius: 4, padding: '5px 7px', fontSize: 9 }}>{name}</div>;
          }) : <span style={{ color: '#9ca3af', fontSize: 11 }}>{loading ? 'Loading uplink interfaces…' : 'No uplink data reported'}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 10, color: '#aaa' }}><span><span style={{ color: '#10b981' }}>■</span> Normal / link up</span><span><span style={{ color: '#9ca3af' }}>■</span> Other / unavailable</span></div>
      {inventoryNotes.map((note, index) => <div key={index} style={{ marginTop: 5, color: '#cbd5e1', fontSize: 9, lineHeight: 1.35 }}>{note}</div>)}
    </div>
  );
}

function MaskedField({ value, label }) {
  const [show, setShow] = useState(false);
  if (!value) return <span style={{ color: '#aaa' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'monospace', fontSize: 13 }}>
      <span>{show ? value : '••••••••••••'}</span>
      <button onClick={() => setShow(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1565C0', fontSize: 14, padding: 0, lineHeight: 1 }} title={show ? 'Hide' : 'Show'}>{show ? '🙈' : '👁'}</button>
    </span>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td style={{ padding: '9px 14px', fontWeight: 600, color: '#555', width: 220, fontSize: 13, whiteSpace: 'nowrap' }}>{label}</td>
      <td style={{ padding: '9px 14px', color: '#222', fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit' }}>{value ?? '—'}</td>
    </tr>
  );
}

function StatusBadge({ online }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: online ? '#e8f5e9' : '#ffebee', color: online ? '#2e7d32' : '#c62828', border: `1px solid ${online ? '#a5d6a7' : '#ef9a9a'}` }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: online ? '#2e7d32' : '#c62828', boxShadow: online ? '0 0 4px #2e7d32' : 'none' }} />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

// SmartOLT-style action button
function ActionBtn({ onClick, color = '#1565C0', children, style = {} }) {
  return (
    <button onClick={onClick} style={{ background: color, color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, ...style }}>
      {children}
    </button>
  );
}

// SmartOLT-style outline button
function OutlineBtn({ onClick, children, style = {}, color = '#1565C0' }) {
  return (
    <button onClick={onClick} style={{ background: '#fff', color, border: `1px solid ${color}`, borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, ...style }}>
      {children}
    </button>
  );
}

const TABS = [
  { id: 'details', label: 'OLT details' },
  { id: 'cards', label: 'OLT cards' },
  { id: 'pon', label: 'PON ports' },
  { id: 'uplink', label: 'Uplink' },
  { id: 'vlans', label: 'VLANs' },
  { id: 'ip-pools', label: 'ONU IP Pools' },
  { id: 'acls', label: 'Remote ACLs' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'voip', label: 'VoIP profiles' },
  { id: 'settings', label: 'Settings' },
  { id: 'advanced', label: 'Advanced' },
];

// ─── OLT CARDS TAB ───────────────────────────────────────────────────────────
function OLTCardsTab({ oltId, api, showToast }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState(null);

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/cards`);
      setCards(d.cards || []);
      setNote(d.note || null);
    } catch (e) {
      setNote('Failed to load cards');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const handleRefresh = async () => {
    showToast('Running SNMP walk on OLT — please wait...', 'info');
    setLoading(true);
    try {
      const r = await api.post(`/api/noc/olts/${oltId}/cards/refresh`, {});
      if (r.status === 'ok') {
        showToast(r.message || 'Card info refreshed', 'success');
        setCards(r.cards || []);
        setNote(null);
      } else {
        showToast(r.message || 'SNMP walk failed', 'error');
        setNote(r.message || 'SNMP walk failed');
      }
    } catch (e) {
      showToast('Failed to refresh cards', 'error');
      setNote('Failed to refresh cards');
    } finally {
      setLoading(false);
    }
  };

  const handleReboot = async (slot) => {
    if (!window.confirm(`Reboot slot ${slot}?`)) return;
    const r = await api.post(`/api/noc/olts/${oltId}/cards/${slot}/reboot`, {});
    showToast(r.message || `Reboot queued for slot ${slot}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <ActionBtn onClick={handleRefresh}>Refresh OLT cards info</ActionBtn>
        <ActionBtn onClick={() => showToast('Detecting new cards...', 'info')} color="#6c757d">Detect new cards</ActionBtn>
      </div>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading cards...</div>
      ) : note && cards.length === 0 ? (
        <div style={{ padding: 24, background: '#fff3cd', borderRadius: 6, color: '#856404', fontSize: 13 }}>
          ⚠️ {note} — <button onClick={handleRefresh} style={{ background: 'none', border: 'none', color: '#1565C0', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>Refresh now</button>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              {['Slot', 'Type', 'Real type', 'Ports', 'SW', 'Status', 'Role', 'Info updated', ''].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cards.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 13 }}>No cards found — run Refresh to scan OLT</td></tr>
            ) : cards.map((card, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '9px 12px', fontSize: 13, color: '#1565C0', fontWeight: 600 }}>{card.slot}</td>
                <td style={{ padding: '9px 12px', fontSize: 13 }}>{card.card_type || '—'}</td>
                <td style={{ padding: '9px 12px', fontSize: 13 }}>{card.real_type || '—'}</td>
                <td style={{ padding: '9px 12px', fontSize: 13 }}>{card.ports ?? '—'}</td>
                <td style={{ padding: '9px 12px', fontSize: 13, fontFamily: 'monospace', fontSize: 12 }}>{card.sw_version || ''}</td>
                <td style={{ padding: '9px 12px', fontSize: 13 }}>
                  {card.status === 'Offline' || card.status === 'offline' ? (
                    <span style={{ background: '#FFEBEE', color: '#c62828', fontWeight: 600, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>Offline</span>
                  ) : card.status === 'Normal' || card.status === 'normal' ? (
                    <span style={{ background: '#FFF3E0', color: '#E65100', fontWeight: 600, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>Normal</span>
                  ) : card.status === 'Failed' || card.status === 'failed' ? (
                    <span style={{ background: '#FFEBEE', color: '#b71c1c', fontWeight: 600, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>Failed</span>
                  ) : card.status ? (
                    <span style={{ background: '#F3F4F6', color: '#374151', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{card.status}</span>
                  ) : '—'}
                </td>
                <td style={{ padding: '9px 12px', fontSize: 13 }}>{card.role || ''}</td>
                <td style={{ padding: '9px 12px', fontSize: 12, color: '#666' }}>{card.info_updated ? new Date(card.info_updated).toLocaleString() : '—'}</td>
                <td style={{ padding: '9px 12px' }}>
                  <ActionBtn onClick={() => handleReboot(card.slot)}>Reboot-card</ActionBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── PON PORTS TAB ───────────────────────────────────────────────────────────
function PONPortsTab({ oltId, api, showToast, cards }) {
  const [ports, setPorts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [scanning, setScanning] = React.useState(false);
  const [note, setNote] = React.useState(null);
  const [selectedSlot, setSelectedSlot] = React.useState("");
  const loadPorts = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedSlot ? "/api/noc/olts/" + oltId + "/pon-ports-list?slot=" + selectedSlot : "/api/noc/olts/" + oltId + "/pon-ports-list";
      const d = await api.get(url);
      setPorts(d.pon_ports || []);
      setNote(d.note || null);
    } catch (e) {
      setNote("Failed to load PON ports");
    } finally {
      setLoading(false);
    }
  }, [api, oltId, selectedSlot]);
  React.useEffect(() => { loadPorts(); }, [loadPorts]);
  const handleRefresh = async () => {
    setScanning(true);
    showToast("Refreshing PON port info via SNMP...", "info");
    try {
      const d = await api.post("/api/noc/olts/" + oltId + "/pon-ports-list/refresh", {});
      setPorts(d.pon_ports || []);
      setNote(null);
      showToast("PON ports refreshed: " + (d.pon_ports||[]).length + " ports found", "success");
    } catch (e) {
      showToast("Refresh failed: " + (e.message||"error"), "error");
    } finally {
      setScanning(false);
    }
  };
  const slotOptions = cards && cards.length > 0 ? cards.map(c => c.slot) : [...new Set((ports||[]).map(p=>p.slot))].sort((a,b)=>a-b);
  const filteredPorts = selectedSlot ? ports.filter(p => String(p.slot) === String(selectedSlot)) : ports;
  const slotGroups = {};
  filteredPorts.forEach(p => {
    const s = p.slot;
    if (!slotGroups[s]) slotGroups[s] = [];
    slotGroups[s].push(p);
  });
  const SignalBars = ({ dbm }) => {
    if (dbm === null || dbm === undefined) return React.createElement("span", {style:{color:"#aaa"}}, "\u2014");
    const abs = Math.abs(dbm);
    const bars = abs < 20 ? 5 : abs < 25 ? 4 : abs < 28 ? 3 : abs < 31 ? 2 : 1;
    const color = bars >= 4 ? "#2e7d32" : bars >= 3 ? "#f57c00" : "#c62828";
    return React.createElement("span", {style:{display:"inline-flex",alignItems:"center",gap:4}},
      React.createElement("span", {style:{color:color,fontWeight:600,fontSize:13}}, dbm.toFixed(2)),
      React.createElement("span", {style:{display:"inline-flex",alignItems:"flex-end",gap:1}},
        [1,2,3,4,5].map(b=>React.createElement("span",{key:b,style:{width:3,height:4+b*2,background:b<=bars?color:"#ddd",borderRadius:1,display:"inline-block"}}))
      )
    );
  };
  const LoadBar = ({ total, maxOnu }) => {
    const max = maxOnu || 128;
    const pct = max > 0 ? Math.round((total / max) * 100) : 0;
    const barColor = pct > 80 ? "#dc3545" : pct > 50 ? "#fd7e14" : "#28a745";
    return React.createElement("div", {style:{display:"flex",alignItems:"center",gap:6}},
      React.createElement("div", {style:{width:90,height:14,background:"#e9ecef",borderRadius:3,overflow:"hidden",position:"relative"}},
        React.createElement("div", {style:{width:pct+"%",height:"100%",background:barColor,transition:"width 0.3s"}}),
        React.createElement("span", {style:{position:"absolute",top:0,left:0,right:0,textAlign:"center",fontSize:10,lineHeight:"14px",color:"#333",fontWeight:600}}, total+"/"+max+" ("+pct+"%)")
      )
    );
  };
  const getAdminColor = (state) => {
    if (!state) return "#888";
    const s = state.toLowerCase();
    if (s.includes("enabled")) return "#2e7d32";
    if (s.includes("disabled")) return "#c62828";
    return "#888";
  };
  const getStatusColor = (status) => {
    if (!status) return "#888";
    const s = status.toLowerCase();
    if (s.includes("up")) return "#2e7d32";
    if (s.includes("down")) return "#c62828";
    return "#888";
  };
  return React.createElement("div", null,
    React.createElement("div", {style:{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}},
      React.createElement(ActionBtn, {onClick:handleRefresh,disabled:scanning}, scanning?"\u27f3 Refreshing...":"Refresh PON ports info"),
      React.createElement(ActionBtn, {onClick:()=>showToast("Enable all PON ports \u2014 requires CLI","info"),color:"#1565C0"}, "Enable all PON ports"),
      React.createElement(ActionBtn, {onClick:()=>showToast("AutoFind enabled","info"),color:"#1565C0"}, "Enable AutoFind"),
      React.createElement(ActionBtn, {onClick:()=>showToast("Reboot all ONUs queued","info"),color:"#dc3545"}, "Reboot all ONUs"),
      React.createElement(ActionBtn, {onClick:()=>showToast("Rogue ONU detection started","info"),color:"#fd7e14"}, "\u2699 Rogue ONU detect")
    ),
    React.createElement("div", {style:{display:"flex",alignItems:"center",gap:12,marginBottom:16}},
      React.createElement("label", {style:{fontSize:13,fontWeight:600,color:"#555"}}, "OLT slot"),
      React.createElement("select", {value:selectedSlot,onChange:e=>setSelectedSlot(e.target.value),style:{border:"1px solid #ccc",borderRadius:4,padding:"5px 10px",fontSize:13}},
        React.createElement("option", {value:""}, "Any"),
        slotOptions.map(s=>React.createElement("option",{key:s,value:s},s))
      ),
      scanning && React.createElement("span", {style:{fontSize:12,color:"#888"}}, "\u27f3 Running SNMP walk...")
    ),
    loading ? React.createElement("div", {style:{padding:40,textAlign:"center",color:"#888"}}, React.createElement("div", {style:{fontSize:13}}, "\u27f3 Loading PON ports...")) :
    note && filteredPorts.length === 0 ? React.createElement("div", null,
      React.createElement("div", {style:{padding:24,background:"#fff3cd",borderRadius:6,color:"#856404",fontSize:13,marginBottom:12}},
        "\u26a0\ufe0f " + note + " \u2014 ",
        React.createElement("button", {onClick:handleRefresh,style:{background:"none",border:"none",color:"#1565C0",cursor:"pointer",textDecoration:"underline",fontSize:13}}, "Refresh now")
      )
    ) :
    React.createElement("div", null,
      Object.keys(slotGroups).sort((a,b)=>Number(a)-Number(b)).map(slot => {
        const slotPorts = slotGroups[slot];
        const boardCard = cards && cards.find(c => String(c.slot) === String(slot));
        const boardType = boardCard ? (boardCard.board_type || boardCard.type || "") : "";
        return React.createElement("div", {key:slot,style:{marginBottom:24}},
          React.createElement("div", {style:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}},
            React.createElement("div", {style:{fontSize:14,fontWeight:700,color:"#1565C0"}},
              "OLT slot " + slot + (boardType ? ", board type: " + boardType : "")
            ),
            React.createElement("div", {style:{display:"flex",gap:8}},
              React.createElement("button", {onClick:()=>showToast("Configure Max ONUs for slot "+slot,"info"),style:{background:"none",border:"none",color:"#1565C0",cursor:"pointer",fontSize:12,textDecoration:"underline"}}, "\u2699 Configure Max ONUs per PON"),
              React.createElement("button", {onClick:()=>showToast("Reboot all ONUs on slot "+slot,"info"),style:{background:"none",border:"none",color:"#1565C0",cursor:"pointer",fontSize:12,textDecoration:"underline"}}, "\u21ba Reboot all ONUs on slot " + slot)
            )
          ),
          React.createElement("table", {style:{width:"100%",borderCollapse:"collapse",background:"#fff",borderRadius:8,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.08)",fontSize:13}},
            React.createElement("thead", null,
              React.createElement("tr", {style:{background:"#f8f9fa",borderBottom:"2px solid #dee2e6"}},
                ["Port","Type","Admin state","Status","ONUs","Load","Average signal","Description","Properties","Tx power dBm","Action"].map(h=>
                  React.createElement("th", {key:h,style:{padding:"9px 10px",textAlign:"left",fontSize:12,fontWeight:600,color:"#495057",whiteSpace:"nowrap"}}, h)
                )
              )
            ),
            React.createElement("tbody", null,
              slotPorts.map((p,i)=>React.createElement("tr", {key:i,style:{borderBottom:"1px solid #f0f0f0"}},
                React.createElement("td", {style:{padding:"8px 10px",color:"#1565C0",fontWeight:600}}, p.port_no),
                React.createElement("td", {style:{padding:"8px 10px",color:"#555"}}, p.port_type||"GPON"),
                React.createElement("td", {style:{padding:"8px 10px"}}, React.createElement("span", {style:{color:getAdminColor(p.admin_state),fontWeight:600}}, p.admin_state||"\u2014")),
                React.createElement("td", {style:{padding:"8px 10px"}}, React.createElement("span", {style:{color:getStatusColor(p.status)}}, p.status||"\u2014")),
                React.createElement("td", {style:{padding:"8px 10px"}},
                  React.createElement("div", {style:{color:"#1565C0",fontSize:12}}, "Online: "+(p.onu_online||0)),
                  React.createElement("div", {style:{color:"#555",fontSize:12}}, "Total: "+(p.onu_total||0))
                ),
                React.createElement("td", {style:{padding:"8px 10px",minWidth:110}}, React.createElement(LoadBar, {total:p.onu_total||0,maxOnu:p.onu_max||128})),
                React.createElement("td", {style:{padding:"8px 10px",minWidth:100}}, React.createElement(SignalBars, {dbm:p.avg_rx_power})),
                React.createElement("td", {style:{padding:"8px 10px",color:"#555",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}, p.description||""),
                React.createElement("td", {style:{padding:"8px 10px",fontSize:12}},
                  React.createElement("div", {style:{color:"#555"}}, "Range: "+(p.range_min||0)+" \u2013 "+(p.range_max||20000)+" m"),
                  React.createElement("div", null, "Rogue ONU detect: ", React.createElement("span", {style:{color:p.rogue_onu_detect==="Enabled"?"#2e7d32":"#888"}}, p.rogue_onu_detect||"Disabled"))
                ),
                React.createElement("td", {style:{padding:"8px 10px",color:p.tx_power?"#1565C0":"#aaa"}}, p.tx_power!==null&&p.tx_power!==undefined?p.tx_power.toFixed(2):"\u2014"),
                React.createElement("td", {style:{padding:"8px 10px",whiteSpace:"nowrap"}},
                  React.createElement(OutlineBtn, {onClick:()=>showToast("Configure port "+p.port_no+" slot "+p.slot,"info")}, "\u2699 Configure"),
                  React.createElement("br"),
                  React.createElement(OutlineBtn, {onClick:()=>showToast("Reboot ONUs on port "+p.port_no,"info")}, "\u21ba Reboot ONUs")
                )
              ))
            )
          )
        );
      })
    )
  );
}
// ─── UPLINK TAB ──────────────────────────────────────────────────────────────
function UplinkTab({ oltId, api, showToast }) {
  const [uplinks, setUplinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState(null);
  const [traffic, setTraffic] = useState({});  // if_index -> {rx_mbps, tx_mbps}
  const [trafficLoading, setTrafficLoading] = useState(false);
  const trafficIntervalRef = React.useRef(null);

  const loadUplinks = React.useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/uplinks-list`);
      setUplinks(d.uplinks || []);
      setNote(d.note || null);
    } catch (e) {
      setNote('Failed to load uplinks');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  const pollTraffic = React.useCallback(async () => {
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/uplinks-traffic`);
      if (d.traffic) {
        const map = {};
        d.traffic.forEach(t => {
          map[t.if_index] = { rx_mbps: t.rx_mbps, tx_mbps: t.tx_mbps, in_octets: t.in_octets, out_octets: t.out_octets };
        });
        setTraffic(map);
      }
    } catch (e) {
      // silently ignore traffic poll errors
    }
  }, [api, oltId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const d = await api.post(`/api/noc/olts/${oltId}/uplinks-list/refresh`, {});
      setUplinks(d.uplinks || []);
      setNote(null);
      showToast('Uplink ports refreshed via SNMP', 'success');
    } catch (e) {
      showToast('SNMP refresh failed: ' + (e.message || 'Unknown error'), 'error');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadUplinks();
  }, [loadUplinks]);

  // Start traffic polling every 10 seconds when tab is active
  useEffect(() => {
    // First poll immediately after uplinks loaded
    if (uplinks.length > 0) {
      pollTraffic();
      trafficIntervalRef.current = setInterval(pollTraffic, 10000);
    }
    return () => {
      if (trafficIntervalRef.current) clearInterval(trafficIntervalRef.current);
    };
  }, [uplinks.length, pollTraffic]);

  const getAdminColor = (state) => {
    if (!state) return '#888';
    return state === 'Enabled' ? '#2e7d32' : '#c62828';
  };

  const getStatusColor = (status) => {
    if (!status) return '#888';
    if (status === 'Up' || status.includes('FullD')) return '#2e7d32';
    return '#c62828';
  };

  const getPortTypeIcon = (type) => {
    if (!type) return '';
    if (type === 'Fiber') return '🔵 ';
    if (type === 'LAG') return '🔗 ';
    return '';
  };

  const formatMbps = (val) => {
    if (val == null) return null;
    if (val >= 1000) return `${(val / 1000).toFixed(2)} Gbps`;
    if (val >= 1) return `${val.toFixed(2)} Mbps`;
    return `${(val * 1000).toFixed(1)} Kbps`;
  };

  const getMbpsBarColor = (mbps) => {
    if (!mbps) return '#ccc';
    if (mbps >= 800) return '#c62828';
    if (mbps >= 500) return '#f57c00';
    if (mbps >= 100) return '#1565C0';
    return '#2e7d32';
  };

  const getMbpsBarWidth = (mbps, maxMbps = 10000) => {
    if (!mbps || mbps <= 0) return 0;
    return Math.min(100, (mbps / maxMbps) * 100);
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ActionBtn onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Refresh uplink ports info'}
        </ActionBtn>
        <span style={{ fontSize: 12, color: '#888' }}>
          Traffic auto-refreshes every 10s
        </span>
      </div>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading uplinks...</div>
      ) : note && uplinks.length === 0 ? (
        <div style={{ padding: 24, background: '#fff3cd', borderRadius: 6, color: '#856404', fontSize: 13 }}>
          ⚠️ {note} — <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={handleRefresh}>Refresh now</span>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              {['Uplink port', 'Description', 'Type', 'Admin state', 'Status', 'Negotiation', 'MTU', 'Traffic Rx ↓', 'Traffic Tx ↑', 'WaveL', 'Temp', 'PVID untag', 'Mode: tagged VLANs', 'Action'].map(h => (
                <th key={h} style={{ padding: '10px 10px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#495057', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {uplinks.length === 0 ? (
              <tr><td colSpan={14} style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 13 }}>No uplink ports found</td></tr>
            ) : uplinks.map((u, i) => {
              const traf = traffic[u.if_index] || {};
              const rxMbps = traf.rx_mbps;
              const txMbps = traf.tx_mbps;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>
                    <div style={{ color: '#1565C0', fontWeight: 600 }}>{u.port_name}</div>
                    {u.is_lag && <div style={{ color: '#666', fontSize: 11 }}>LAG interface</div>}
                    {u.lag_members && (
                      <div style={{ fontSize: 10, color: '#888' }}>
                        {(typeof u.lag_members === 'string' ? u.lag_members.split(',') : []).map(m => (
                          <div key={m} style={{ color: '#1565C0' }}>{m.trim()}</div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', fontSize: 13, color: '#555' }}>{u.description || ''}</td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>
                    <span>{getPortTypeIcon(u.port_type)}{u.port_type || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>
                    <span style={{ color: getAdminColor(u.admin_state), fontWeight: 500 }}>{u.admin_state || 'Enabled'}</span>
                  </td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>
                    <span style={{ color: getStatusColor(u.status), fontWeight: 600 }}>{u.status || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 10px', fontSize: 12, color: '#555' }}>{u.negotiation || 'Auto'}</td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>{u.mtu ? u.mtu.toLocaleString() : '—'}</td>
                  {/* Traffic Rx */}
                  <td style={{ padding: '9px 10px', fontSize: 12, minWidth: 100 }}>
                    {rxMbps != null ? (
                      <div>
                        <div style={{ color: '#1565C0', fontWeight: 600, marginBottom: 2 }}>{formatMbps(rxMbps)}</div>
                        <div style={{ background: '#e9ecef', borderRadius: 3, height: 6, width: 80 }}>
                          <div style={{ background: getMbpsBarColor(rxMbps), height: 6, borderRadius: 3, width: `${getMbpsBarWidth(rxMbps)}%`, transition: 'width 0.5s' }} />
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 11 }}>polling...</span>
                    )}
                  </td>
                  {/* Traffic Tx */}
                  <td style={{ padding: '9px 10px', fontSize: 12, minWidth: 100 }}>
                    {txMbps != null ? (
                      <div>
                        <div style={{ color: '#2e7d32', fontWeight: 600, marginBottom: 2 }}>{formatMbps(txMbps)}</div>
                        <div style={{ background: '#e9ecef', borderRadius: 3, height: 6, width: 80 }}>
                          <div style={{ background: getMbpsBarColor(txMbps), height: 6, borderRadius: 3, width: `${getMbpsBarWidth(txMbps)}%`, transition: 'width 0.5s' }} />
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 11 }}>polling...</span>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>{u.wavelength ?? ''}</td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>{u.temperature ?? ''}</td>
                  <td style={{ padding: '9px 10px', fontSize: 13 }}>{u.pvid ?? ''}</td>
                  <td style={{ padding: '9px 10px', fontSize: 12, color: '#555' }}>
                    {u.tagged_vlans ? <span>Trunk: {u.tagged_vlans}</span> : u.untagged_vlan ? <span>Access: {u.untagged_vlan}</span> : ''}
                  </td>
                  <td style={{ padding: '9px 10px' }}>
                    <OutlineBtn onClick={() => showToast(`Configure ${u.port_name}`, 'info')}>⚙ Configure</OutlineBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
// ─── VLANs TAB// ─── VLANs TAB// ─── VLANs TAB ───────────────────────────────────────────────────────────────
function VLANsTab({ oltId, api, showToast }) {
  const [vlans, setVlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newVlan, setNewVlan] = useState({ vlan_id: '', description: '' });

  const loadVlans = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/vlans-list`);
      setVlans(d.vlans || []);
    } catch (e) {
      showToast('Failed to load VLANs', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  useEffect(() => { loadVlans(); }, [loadVlans]);

  const handleAddVlan = async () => {
    if (!newVlan.vlan_id) { showToast('VLAN ID is required', 'error'); return; }
    try {
      await api.post(`/api/noc/olts/${oltId}/vlans-list`, { vlan_id: parseInt(newVlan.vlan_id), description: newVlan.description });
      showToast(`VLAN ${newVlan.vlan_id} added`);
      setShowAddModal(false);
      setNewVlan({ vlan_id: '', description: '' });
      loadVlans();
    } catch (e) {
      showToast('Failed to add VLAN', 'error');
    }
  };

  const handleDeleteVlan = async (vlanId) => {
    if (!window.confirm(`Delete VLAN ${vlanId}?`)) return;
    try {
      await api.delete(`/api/noc/olts/${oltId}/vlans-list/${vlanId}`);
      showToast(`VLAN ${vlanId} deleted`);
      loadVlans();
    } catch (e) {
      showToast('Failed to delete VLAN', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <ActionBtn onClick={() => setShowAddModal(true)}>+ Add VLAN</ActionBtn>
        <ActionBtn onClick={() => showToast('Add multiple VLANs — coming soon', 'info')} color="#1565C0">+ Add multiple VLANs</ActionBtn>
        <ActionBtn onClick={() => showToast('Delete multiple VLANs — select rows first', 'info')} color="#dc3545">− Delete multiple VLANs</ActionBtn>
      </div>
      <div style={{ fontSize: 12, color: '#1565C0', marginBottom: 12 }}>
        VLANs added here will not be applied to the Uplink ports automatically. Go to Uplink and tag the VLANs on the interfaces you want.
      </div>

      {showAddModal && (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 16, maxWidth: 400 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Add VLAN</div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>VLAN ID *</label>
            <input type="number" value={newVlan.vlan_id} onChange={e => setNewVlan(p => ({ ...p, vlan_id: e.target.value }))} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px', fontSize: 13, width: '100%' }} placeholder="e.g. 100" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label>
            <input type="text" value={newVlan.description} onChange={e => setNewVlan(p => ({ ...p, description: e.target.value }))} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px', fontSize: 13, width: '100%' }} placeholder="Optional" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <ActionBtn onClick={handleAddVlan}>Add</ActionBtn>
            <OutlineBtn onClick={() => setShowAddModal(false)}>Cancel</OutlineBtn>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading VLANs...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              {['VLAN-ID', 'Default for', 'Description', 'Used for IPTV', 'Used for Mgmt/VoIP', 'DHCP Snooping', 'LAN to LAN', 'ONUs', 'Action'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vlans.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 13 }}>No VLANs configured — click "+ Add VLAN" to add one</td></tr>
            ) : vlans.map((v, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '9px 12px', fontSize: 13, color: '#1565C0', fontWeight: 600 }}>{v.vlan_id}</td>
                <td style={{ padding: '9px 12px', fontSize: 13, color: '#888' }}>{v.default_for || 'None selected'}</td>
                <td style={{ padding: '9px 12px', fontSize: 13 }}>{v.description || ''}</td>
                <td style={{ padding: '9px 12px', textAlign: 'center' }}><input type="checkbox" checked={!!v.used_for_iptv} readOnly /></td>
                <td style={{ padding: '9px 12px', textAlign: 'center' }}><input type="checkbox" checked={!!v.used_for_mgmt_voip} readOnly /></td>
                <td style={{ padding: '9px 12px', textAlign: 'center' }}><input type="checkbox" checked={!!v.dhcp_snooping} readOnly /></td>
                <td style={{ padding: '9px 12px', textAlign: 'center' }}><input type="checkbox" checked={!!v.lan_to_lan} readOnly /></td>
                <td style={{ padding: '9px 12px', fontSize: 13, color: '#1565C0' }}>{v.onu_count ?? 0}</td>
                <td style={{ padding: '9px 12px' }}>
                  <ActionBtn onClick={() => handleDeleteVlan(v.vlan_id)} color="#dc3545">Delete</ActionBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── ONU IP POOLS TAB ────────────────────────────────────────────────────────
function ONUIPPoolsTab({ oltId, api, showToast }) {
  const [activeSubTab, setActiveSubTab] = useState('mgmt');
  const [data, setData] = useState({ mgmt_pools: [], wan_static_ips: [] });
  const [loading, setLoading] = useState(true);

  const loadPools = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/onu-ip-pools-list`);
      setData(d);
    } catch (e) {
      showToast('Failed to load IP pools', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  useEffect(() => { loadPools(); }, [loadPools]);

  const pools = activeSubTab === 'mgmt' ? (data.mgmt_pools || []) : (data.wan_static_ips || []);
  const totalPools = pools.length;
  const usedIPs = pools.reduce((s, p) => s + (p.used_ips || 0), 0);
  const reservedIPs = pools.reduce((s, p) => s + (p.reserved_ips || 0), 0);
  const availableIPs = pools.reduce((s, p) => s + (p.available_ips || 0), 0);
  const needsAttention = pools.filter(p => p.needs_attention).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
        <button onClick={() => setActiveSubTab('mgmt')} style={{ background: activeSubTab === 'mgmt' ? '#1565C0' : '#fff', color: activeSubTab === 'mgmt' ? '#fff' : '#1565C0', border: '1px solid #1565C0', borderRadius: '4px 0 0 4px', padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>☰ ONU MGMT IPs</button>
        <button onClick={() => setActiveSubTab('wan')} style={{ background: activeSubTab === 'wan' ? '#2e7d32' : '#fff', color: activeSubTab === 'wan' ? '#fff' : '#2e7d32', border: '1px solid #2e7d32', borderRadius: '0 4px 4px 0', padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>⊕ ONU WAN Static IPs</button>
      </div>

      {activeSubTab === 'mgmt' && (
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>ONU Management IP Pools</div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Define IP address pools for Management &amp; VoIP services. These pools will be used for Static IP allocation.</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {[['Pools', totalPools], ['Needs attention', needsAttention], ['Used IPs', usedIPs], ['Reserved IPs', reservedIPs], ['Available IPs', availableIPs]].map(([label, val]) => (
              <div key={label} style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 16px', minWidth: 100 }}>
                <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#333' }}>{val}</div>
              </div>
            ))}
          </div>
          <ActionBtn onClick={() => showToast('Add Mgmt IPs pool — coming soon', 'info')}>+ Add Mgmt IPs</ActionBtn>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : pools.length === 0 ? (
        <div style={{ padding: 24, color: '#888', fontSize: 13, marginTop: 12 }}>No pools configured.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginTop: 12 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              {['', 'Network (CIDR)', 'Gateway', 'DNS 1', 'DNS 2', 'VLAN', 'Allocation', 'Usage', 'Action'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pools.map((p, i) => {
              const usagePct = p.total_ips > 0 ? Math.round((p.used_ips / p.total_ips) * 100) : 0;
              return (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '9px 12px' }}>▼</td>
                  <td style={{ padding: '9px 12px', fontSize: 13 }}>
                    <span style={{ background: '#e3f2fd', color: '#1565C0', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontFamily: 'monospace' }}>{p.network_cidr}</span>
                    {p.olt_count > 1 && <span style={{ marginLeft: 6, background: '#e8f5e9', color: '#2e7d32', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>↔ {p.olt_count} OLTs</span>}
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: 13, fontFamily: 'monospace' }}>{p.gateway || '—'}</td>
                  <td style={{ padding: '9px 12px', fontSize: 13, fontFamily: 'monospace' }}>{p.dns1 || '—'}</td>
                  <td style={{ padding: '9px 12px', fontSize: 13, fontFamily: 'monospace' }}>{p.dns2 || '—'}</td>
                  <td style={{ padding: '9px 12px', fontSize: 13 }}>{p.vlan || '—'}</td>
                  <td style={{ padding: '9px 12px', minWidth: 160 }}>
                    <div style={{ background: '#e0e0e0', borderRadius: 4, height: 10, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(usagePct, 100)}%`, height: '100%', background: usagePct > 90 ? '#dc3545' : '#1565C0' }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{p.used_ips || 0} used / {p.available_ips || 0} free / {p.total_ips || 0} IPs</div>
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ background: usagePct > 90 ? '#dc3545' : '#6c757d', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>{usagePct}%{usagePct > 90 ? '+' : ''}</span>
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <OutlineBtn onClick={() => showToast(`View IPs for ${p.network_cidr}`, 'info')}>☰ View IPs</OutlineBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── PROFILES TAB ────────────────────────────────────────────────────────────
function ProfilesTab({ oltId, api, showToast }) {
  const [profiles, setProfiles] = useState({ tcont_profiles: [], line_profiles: [], service_profiles: [] });
  const [loading, setLoading] = useState(true);
  const [expandedTcont, setExpandedTcont] = useState({});

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/profiles-list`);
      setProfiles(d);
    } catch (e) {
      showToast('Failed to load profiles', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading profiles...</div>;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <ActionBtn onClick={loadProfiles}>🔍 Scan OLT profiles</ActionBtn>
      </div>

      {/* T-CONT Profiles */}
      {profiles.tcont_profiles && profiles.tcont_profiles.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057', width: 32 }}></th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>Profile name</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>Default for ONU types</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>ONUs</th>
              </tr>
            </thead>
            <tbody>
              {profiles.tcont_profiles.map((p, i) => (
                <React.Fragment key={i}>
                  <tr style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={() => setExpandedTcont(prev => ({ ...prev, [i]: !prev[i] }))}>
                    <td style={{ padding: '9px 12px', fontSize: 13 }}>{expandedTcont[i] ? '▼' : '▶'}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, color: '#1565C0' }}>{p.profile_name}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13 }}>{p.default_for_onu_types || ''}</td>
                    <td style={{ padding: '9px 12px', fontSize: 13, color: '#1565C0' }}>{p.onu_count ?? 0}</td>
                  </tr>
                  {expandedTcont[i] && (
                    <tr>
                      <td colSpan={4} style={{ padding: '0 12px 12px 32px', background: '#f8f9fa' }}>
                        <div style={{ fontSize: 12, color: '#666' }}>T-CONT details: {JSON.stringify(p.details || {})}</div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Line Profiles */}
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, marginTop: 16 }}>Line profiles</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
        <thead>
          <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
            {['Profile name', 'Source', 'Mapping', 'Visible', 'Last verified'].map(h => (
              <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {profiles.line_profiles && profiles.line_profiles.length > 0 ? profiles.line_profiles.map((p, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: p.is_system ? '#e3f2fd' : '#fff' }}>
              <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: p.is_system ? 700 : 400 }}>{p.profile_name}</td>
              <td style={{ padding: '9px 12px' }}>
                {p.source && <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{p.source}</span>}
              </td>
              <td style={{ padding: '9px 12px', fontSize: 12, color: '#555' }}>{p.mapping || '—'}</td>
              <td style={{ padding: '9px 12px', fontSize: 13 }}>{p.visible != null ? (p.visible ? 'Yes' : 'No') : '-'}</td>
              <td style={{ padding: '9px 12px', fontSize: 12, color: '#666' }}>{p.last_verified ? new Date(p.last_verified).toLocaleString() : '-'}</td>
            </tr>
          )) : (
            <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>No line profiles found — run Scan to discover</td></tr>
          )}
        </tbody>
      </table>

      {/* Service Profiles */}
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Service profiles</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <thead>
          <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
            {['Profile name', 'Source', 'Visible'].map(h => (
              <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {profiles.service_profiles && profiles.service_profiles.length > 0 ? profiles.service_profiles.map((p, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '9px 12px', fontSize: 13 }}>{p.profile_name}</td>
              <td style={{ padding: '9px 12px' }}>
                {p.source && <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>{p.source}</span>}
              </td>
              <td style={{ padding: '9px 12px', fontSize: 13 }}>{p.visible != null ? (p.visible ? 'Yes' : 'No') : '-'}</td>
            </tr>
          )) : (
            <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>No profiles found on the OLT</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── ADVANCED TAB ────────────────────────────────────────────────────────────
function AdvancedTab({ oltId, api, showToast }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/advanced`);
      setSettings(d);
      setForm(d);
    } catch (e) {
      showToast('Failed to load advanced settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    try {
      await api.put(`/api/noc/olts/${oltId}/advanced`, form);
      showToast('Advanced settings saved');
      setEditing(false);
      loadSettings();
    } catch (e) {
      showToast('Failed to save settings', 'error');
    }
  };

  const boolVal = (v) => v ? 'Yes' : 'No';
  const editBool = (key) => (
    <select value={form[key] ? 'yes' : 'no'} onChange={e => setForm(p => ({ ...p, [key]: e.target.value === 'yes' }))} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px', fontSize: 13 }}>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
  const editText = (key) => (
    <input value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px', fontSize: 13, width: 200 }} />
  );
  const editNum = (key) => (
    <input type="number" value={form[key] ?? ''} onChange={e => setForm(p => ({ ...p, [key]: parseInt(e.target.value) }))} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '4px 8px', fontSize: 13, width: 100 }} />
  );

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading advanced settings...</div>;
  if (!settings) return null;

  const rows = [
    { label: 'Show admin-disabled ONUs in Unconfigured', key: 'show_admin_disabled_in_unconfigured', type: 'bool' },
    { label: 'Send ONUs description to OLT config', key: 'send_onus_description', type: 'text' },
    { label: 'Use DHCP Option82', key: 'use_dhcp_option82', type: 'bool' },
    { label: 'DHCP Option82 field', key: 'dhcp_option82_field', type: 'text' },
    { label: 'Use DHCP Option82 for Mgmt', key: 'use_dhcp_option82_mgmt', type: 'bool' },
    { label: 'DHCP Option82 field for Mgmt', key: 'dhcp_option82_field_mgmt', type: 'text' },
    { label: 'Use PPPoE-plus', key: 'use_pppoe_plus', type: 'bool' },
    { label: 'ONU IP source-guard', key: 'onu_ip_source_guard', type: 'text' },
    { label: 'Use max-mac-learn', key: 'use_max_mac_learn', type: 'bool' },
    { label: 'Max MACs allowed on internet vport per ONU', key: 'max_macs_per_onu', type: 'num' },
    { label: 'Use MAC allow list', key: 'use_mac_allow_list', type: 'bool' },
    { label: 'Use MAC drop list', key: 'use_mac_drop_list', type: 'bool' },
    { label: 'Use Ports filtering ACL', key: 'use_ports_filtering_acl', type: 'bool' },
    { label: 'Ports filtering ACL', key: 'ports_filtering_acl', type: 'text' },
    { label: 'Signal RX level "warning" (dBm)', key: 'signal_rx_warning_dbm', type: 'num' },
    { label: 'Signal RX level "critical" (dBm)', key: 'signal_rx_critical_dbm', type: 'num' },
    { label: 'Separate IP-hosts for VoIP/Mgmt', key: 'separate_ip_hosts_voip_mgmt', type: 'bool' },
    { label: 'OLT temperature level "warning" (°C)', key: 'olt_temp_warning_c', type: 'num' },
    { label: 'OLT temperature level "critical" (°C)', key: 'olt_temp_critical_c', type: 'num' },
    { label: 'Use CVLAN-ID option', key: 'use_cvlan_id_option', type: 'bool' },
    { label: 'Use SVLAN-ID option', key: 'use_svlan_id_option', type: 'bool' },
    { label: 'Default tag-transform mode', key: 'default_tag_transform_mode', type: 'text' },
    { label: 'Use "other-all" TLS VLAN', key: 'use_other_all_tls_vlan', type: 'bool' },
  ];

  const displayVal = (row) => {
    const v = settings[row.key];
    if (row.type === 'bool') return boolVal(v);
    return v != null && v !== '' ? String(v) : '';
  };

  const editVal = (row) => {
    if (row.type === 'bool') return editBool(row.key);
    if (row.type === 'num') return editNum(row.key);
    return editText(row.key);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <ActionBtn onClick={handleSave}>Save</ActionBtn>
            <OutlineBtn onClick={() => { setEditing(false); setForm(settings); }}>Cancel</OutlineBtn>
          </div>
        ) : (
          <ActionBtn onClick={() => setEditing(true)}>Edit Advanced</ActionBtn>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <thead>
          <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
            <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057', width: '50%' }}>OLT setting</th>
            <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#495057' }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ padding: '9px 16px', fontSize: 13, color: row.type === 'bool' ? '#1565C0' : '#333', fontWeight: row.type === 'bool' ? 500 : 400 }}>{row.label}</td>
              <td style={{ padding: '9px 16px', fontSize: 13 }}>
                {editing ? editVal(row) : (
                  <span style={{ color: displayVal(row) === 'No' ? '#c62828' : displayVal(row) === 'Yes' ? '#2e7d32' : '#333' }}>
                    {displayVal(row)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── REMOTE ACLs TAB ─────────────────────────────────────────────────────────
function RemoteACLsTab({ oltId, api, showToast }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Remote ACLs</div>
      <div style={{ fontSize: 13 }}>ACL rules will be available after SNMP walk or OLT sync.</div>
      <ActionBtn onClick={() => showToast('Sync ACLs — requires SNMP walk', 'info')} style={{ marginTop: 16 }}>🔄 Sync from OLT</ActionBtn>
    </div>
  );
}

// ─── VoIP PROFILES TAB ───────────────────────────────────────────────────────
function VoIPProfilesTab({ oltId, api, showToast }) {
  return (
    <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📞</div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>VoIP Profiles</div>
      <div style={{ fontSize: 13 }}>VoIP profile data will be available after SNMP walk or OLT sync.</div>
      <ActionBtn onClick={() => showToast('Sync VoIP profiles — requires SNMP walk', 'info')} style={{ marginTop: 16 }}>🔄 Sync from OLT</ActionBtn>
    </div>
  );
}

// ─── OLT SETTINGS TAB ─────────────────────────────────────────────────────────
function OLTSettingsTab({ olt, oltId, api, showToast }) {
  const [preview, setPreview] = React.useState(false);
  const [draft, setDraft] = React.useState({ managementIp: olt?.ip_address || olt?.ip || '', netmask: '', gateway: '', telnet: '23', ssh: '22', snmp: '161', vlan: '', uplink: '', board: '', ponPort: '', lineProfile: '', serviceProfile: '', ntp: '' });
  const isGateway = Boolean(olt?.gateway_linked || olt?.router_host || olt?.transport === 'routeros_telnet' || String(olt?.ip_address || olt?.ip || '').startsWith('192.168.'));
  const transport = isGateway ? 'MikroTik Gateway' : 'Direct Public IP';
  const ip = olt?.ip_address || olt?.ip || '—';
  const rows = [
    ['OLT name', olt?.name],
    ['Management IP / FQDN', ip],
    ['Connection path', transport],
    ['Gateway route', isGateway ? 'ERP → MikroTik Gateway → OLT' : 'ERP → Public OLT endpoint'],
    ['Telnet port', olt?.telnet_port || olt?.tcp_port || 23],
    ['SSH port', olt?.ssh_port || 22],
    ['SNMP version', olt?.snmp_version || 'v2c'],
    ['SNMP UDP port', olt?.snmp_udp || olt?.snmp_port || 161],
    ['SNMP community', olt?.snmp_community ? 'Configured (hidden)' : 'Not configured'],
  ];
  const cards = [
    { title: 'Connection & transport', body: 'Choose the verified path first. Private management IPs stay Gateway-only; public OLTs use the direct endpoint.', color: '#1565C0' },
    { title: 'Management IP / VLAN / gateway', body: 'Preview management addressing and VLAN parameters. Applying changes can interrupt access and requires a separate confirmation.', color: '#0f766e' },
    { title: 'Access control', body: 'Credentials are masked. Password, Telnet/SSH service, and source-interface changes are protected settings.', color: '#7c3aed' },
    { title: 'SNMP monitoring', body: 'Read-only community and poll parameters are shown for monitoring; SNMP writes are not issued by this panel.', color: '#1b5e20' },
    { title: 'GPON / boards / PON ports', body: 'Use the existing Cards and PON ports tabs for read-only inventory and refresh operations.', color: '#c2410c' },
    { title: 'Service/profile preview', body: 'ONU line/service profile changes must be reviewed against the exact MA5600T firmware before any apply action.', color: '#334155' },
  ];
  return <div>
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div><div style={{ fontSize: 16, fontWeight: 800, color: '#12385f' }}>OLT Settings Panel</div><div style={{ color: '#475569', fontSize: 12, marginTop: 5 }}>Firmware-aware settings workspace for OLT ID {oltId}. Read-only preview is available now; device-side writes remain confirmation-gated.</div></div>
        <span style={{ background: isGateway ? '#d1fae5' : '#dbeafe', color: isGateway ? '#065f46' : '#1e40af', borderRadius: 999, padding: '6px 10px', fontSize: 11, fontWeight: 800 }}>{transport}</span>
      </div>
    </div>
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ background: '#37474f', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>Verified connection snapshot</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>{rows.map(([label,value]) => <InfoRow key={label} label={label} value={value} mono={label.includes('IP') || label.includes('port')} />)}</tbody></table>
    </div>
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: 16, marginBottom: 16 }}>
      <div style={{ fontWeight: 800, color: '#0f2742', fontSize: 14, marginBottom: 5 }}>Draft settings — preview only</div>
      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>Enter proposed values to validate and preview. These fields stay in browser memory and are not sent to the OLT.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 10 }}>
        {[['managementIp','1. Management IP'],['netmask','1. Netmask'],['gateway','1. Default gateway'],['telnet','2. Telnet port'],['ssh','2. SSH port'],['snmp','3. SNMP UDP port'],['vlan','4. VLAN ID'],['uplink','4. Uplink port'],['board','5. Board/slot'],['ponPort','5. PON port'],['lineProfile','6. ONU line profile'],['serviceProfile','6. Service profile'],['ntp','7. NTP server']].map(([key,label]) => <label key={key} style={{ color: '#475569', fontSize: 11, fontWeight: 700 }}>{label}<input value={draft[key]} onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))} placeholder='Not set' style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '7px 8px', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: 12 }} /></label>)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}><OutlineBtn onClick={() => { setPreview(true); showToast('Draft validated locally; no device command sent', 'info'); }} color='#1565C0'>Validate & preview</OutlineBtn><OutlineBtn onClick={() => { setDraft({ managementIp: olt?.ip_address || olt?.ip || '', netmask: '', gateway: '', telnet: '23', ssh: '22', snmp: '161', vlan: '', uplink: '', board: '', ponPort: '', lineProfile: '', serviceProfile: '', ntp: '' }); setPreview(false); showToast('Draft cleared', 'info'); }} color='#64748b'>Clear draft</OutlineBtn></div>
      {preview && <div style={{ marginTop: 12, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 6, padding: 12, fontFamily: 'monospace', fontSize: 11, color: '#334155', whiteSpace: 'pre-wrap' }}>PREVIEW ONLY — no command sent{`\n`}Transport: {transport}{`\n`}Target: {ip}{`\n`}Proposed groups: 1 Management · 2 Access · 3 SNMP · 4 VLAN/Uplink · 5 GPON/PON · 6 ONU Profiles · 7 NTP{`\n`}Apply: BLOCKED until exact firmware allowlist, explicit authorized confirmation, and audit endpoint are enabled.</div>}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 12, marginBottom: 16 }}>{cards.map(card => <div key={card.title} style={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${card.color}`, borderRadius: 8, padding: 14 }}><div style={{ color: '#0f2742', fontWeight: 800, fontSize: 13 }}>{card.title}</div><div style={{ color: '#64748b', fontSize: 12, lineHeight: 1.55, marginTop: 7 }}>{card.body}</div></div>)}</div>
    <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 14 }}>
      <div style={{ color: '#9a3412', fontWeight: 800, fontSize: 13 }}>Guarded device change workflow</div>
      <div style={{ color: '#7c2d12', fontSize: 12, lineHeight: 1.55, marginTop: 5 }}>Preview commands → review risk and exact firmware support → explicit authorized confirmation → apply → verify → audit log. Management IP/default route, credentials, service enable/disable, firmware, ONU deletion, and `save` remain blocked from one-click execution.</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}><OutlineBtn onClick={() => { setPreview(!preview); showToast(preview ? 'Settings preview closed' : 'Read-only settings preview opened', 'info'); }} color='#c2410c'>{preview ? 'Hide change preview' : 'Preview change workflow'}</OutlineBtn></div>
      {preview && <div style={{ marginTop: 12, padding: 12, background: '#fff', border: '1px dashed #fb923c', borderRadius: 6, color: '#7c2d12', fontSize: 12 }}>No device command is prepared or sent. Add an approved setting and exact firmware target only after the operator confirms the intended change.</div>}
    </div>
  </div>;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function OLTDetailPage({ oltId, prefetchedOlt = null, prefetchedSnmp = null, onBack, onReady }) {
  const api = useApi();
  const [activeTab, setActiveTab] = useState('details');
  const [olt, setOlt] = useState(() => prefetchedOlt || null);
  const [snmp, setSnmp] = useState(() => prefetchedSnmp || null);
  const [loading, setLoading] = useState(() => !prefetchedOlt);
  const [snmpLoading, setSnmpLoading] = useState(false);
  const [snmpPolled, setSnmpPolled] = useState(() => Boolean(prefetchedSnmp));
  const [toast, setToast] = useState(null);
  const [cards, setCards] = useState([]);
  const [hardwareUplinks, setHardwareUplinks] = useState([]);
  const [hardwareLoading, setHardwareLoading] = useState(true);
  const [hardwareNotes, setHardwareNotes] = useState({ cards: '', uplinks: '' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadOlt = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/detail`);
      setOlt(d);
      if (onReady) onReady();
    } catch (e) {
      showToast('Failed to load OLT details', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, oltId]);

  const pollSNMP = useCallback(async () => {
    setSnmpLoading(true);
    try {
      const d = await api.get(`/api/noc/olts/${oltId}/snmp-poll`);
      setSnmp(d);
      setSnmpPolled(true);
      if (d.reachable) {
        showToast('SNMP poll successful — hardware info updated');
        const updated = await api.get(`/api/noc/olts/${oltId}/detail`);
        setOlt(updated);
      } else {
        showToast(`SNMP unreachable: ${d.error || 'Timeout'}`, 'error');
      }
    } catch (e) {
      showToast('SNMP poll failed', 'error');
    } finally {
      setSnmpLoading(false);
    }
  }, [api, oltId]);

  useEffect(() => { if (!prefetchedOlt) loadOlt(); else if (onReady) onReady(); }, [prefetchedOlt, loadOlt]);
  useEffect(() => { if (olt && !snmpPolled && !prefetchedSnmp) pollSNMP(); }, [olt, snmpPolled, prefetchedSnmp, pollSNMP]);

  // Read-only cached hardware inventory for a truthful OLT visualization.
  useEffect(() => {
    let cancelled = false;
    setHardwareLoading(true);
    Promise.all([
      api.get(`/api/noc/olts/${oltId}/cards`),
      api.get(`/api/noc/olts/${oltId}/uplinks-list`),
    ]).then(([cardData, uplinkData]) => {
      if (!cancelled) {
        setCards(cardData.cards || []);
        setHardwareUplinks(uplinkData.uplinks || []);
        setHardwareNotes({ cards: cardData.note || '', uplinks: uplinkData.note || '' });
      }
    }).catch(() => {}).finally(() => { if (!cancelled) setHardwareLoading(false); });
    return () => { cancelled = true; };
  }, [api, oltId]);
  // Load cards for PON ports slot filter
  useEffect(() => {
    if (activeTab === 'pon' && cards.length === 0) {
      api.get(`/api/noc/olts/${oltId}/cards`).then(d => setCards(d.cards || [])).catch(() => {});
    }
  }, [activeTab, oltId, api, cards.length]);

  if (loading) {
    return (
      <div role='status' aria-live='polite' style={{ minHeight: '55vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6fb', padding: 40, textAlign: 'center', color: '#456' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '28px 38px', boxShadow: '0 8px 26px rgba(15,61,120,.10)', border: '1px solid #dbe4f0' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>📡</div>
          <div style={{ fontWeight: 800, color: '#0f3d78', fontSize: 16 }}>Loading OLT details…</div>
          <div style={{ marginTop: 6, color: '#64748b', fontSize: 12 }}>Reading the selected OLT profile before showing its management tabs.</div>
        </div>
      </div>
    );
  }

  if (!olt) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#c62828' }}>
        <div>OLT not found</div>
        <button onClick={onBack} style={{ marginTop: 12, padding: '8px 20px', cursor: 'pointer' }}>← Back</button>
      </div>
    );
  }

  const isOnline = olt.status === 'online';
  const vendor = snmp?.vendor || ((olt.model || '').includes('MA') ? 'Huawei' : '');
  const modelDetected = snmp?.model_detected || olt.hw_version || olt.model || '';
  const swVersion = snmp?.sw_version || olt.sw_version || olt.last_sync_status || '—';
  const uptime = snmp?.sys_uptime_human || '—';
  const sysName = snmp?.sys_name || olt.name;
  const sysDescr = snmp?.sys_descr || '—';

  return (
    <div style={{ fontFamily: 'Roboto, sans-serif', background: '#f4f6fb', minHeight: '100vh' }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 3000, padding: '10px 20px', borderRadius: 6, background: toast.type === 'error' ? '#c62828' : toast.type === 'info' ? '#1565C0' : '#2e7d32', color: '#fff', fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: 13 }}>{toast.msg}</div>
      )}

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: '#1565C0', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          Back to OLTs list
        </button>
        <div style={{ width: 1, height: 24, background: '#e0e0e0' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>📡</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>{olt.name}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{olt.ip || olt.ip_address}</div>
          </div>
          <StatusBadge online={isOnline} />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ActionBtn onClick={() => showToast('Edit OLT Settings — coming soon', 'info')}>✏️ Edit OLT Settings</ActionBtn>
          <OutlineBtn onClick={() => showToast('History — coming soon', 'info')}>📋 See History</OutlineBtn>
          <OutlineBtn onClick={() => showToast('CLI — coming soon', 'info')}>💻 CLI</OutlineBtn>
          <OutlineBtn onClick={() => showToast('Config Backups — coming soon', 'info')}>💾 Config Backups</OutlineBtn>
          <button onClick={pollSNMP} disabled={snmpLoading} style={{ background: snmpLoading ? '#e0e0e0' : '#e8f5e9', color: snmpLoading ? '#888' : '#2e7d32', border: `1px solid ${snmpLoading ? '#ccc' : '#a5d6a7'}`, borderRadius: 4, padding: '7px 14px', cursor: snmpLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600 }}>
            {snmpLoading ? '⏳ Polling...' : '🔄 SNMP Poll'}
          </button>
        </div>
      </div>

      {/* Tab bar — single row, all tabs visible */}
      <div style={{ background: '#fff', borderBottom: '2px solid #e0e0e0', padding: '0 20px', display: 'flex', gap: 0, overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '12px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 400, color: activeTab === tab.id ? '#1565C0' : '#555', borderBottom: activeTab === tab.id ? '3px solid #1565C0' : '3px solid transparent', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 20 }}>
        {/* OLT DETAILS TAB */}
        {activeTab === 'details' && (
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {snmpPolled && (
                <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 6, background: snmp?.reachable ? '#e8f5e9' : '#fff3cd', color: snmp?.reachable ? '#2e7d32' : '#856404', fontSize: 13, fontWeight: 600, border: `1px solid ${snmp?.reachable ? '#a5d6a7' : '#ffc107'}` }}>
                  {snmp?.reachable ? '✅ SNMP reachable — showing live data' : `⚠️ ${snmp?.error || 'SNMP unreachable'} — Showing stored data only`}
                </div>
              )}

              {/* Connection Settings */}
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: '#1565C0', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>🔗 Connection Settings</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <InfoRow label="OLT Name" value={olt.name} />
                    <InfoRow label="OLT IP / FQDN" value={olt.ip || olt.ip_address} mono />
                    <InfoRow label="System Name (SNMP)" value={sysName} />
                    <InfoRow label="Reachable via VPN" value={olt.vpn_enabled ? '✅ Yes' : '❌ No'} />
                    <InfoRow label="Status" value={<StatusBadge online={isOnline} />} />
                  </tbody>
                </table>
              </div>

              {/* Telnet / SSH */}
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: '#37474f', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>🖥️ Telnet / SSH Access</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <InfoRow label="Telnet TCP Port" value={olt.tcp_port || olt.telnet_port || 23} />
                    <InfoRow label="Telnet Username" value={olt.telnet_username} />
                    <InfoRow label="Telnet Password" value={<MaskedField value={olt.telnet_password} />} />
                    <InfoRow label="Telnet Enable Password" value={<MaskedField value={olt.telnet_enable_password} />} />
                    <InfoRow label="SSH Port" value={olt.ssh_port || 22} />
                    <InfoRow label="SSH Username" value={olt.ssh_username} />
                    <InfoRow label="SSH Password" value={<MaskedField value={olt.ssh_password} />} />
                  </tbody>
                </table>
              </div>

              {/* SNMP */}
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: '#1b5e20', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>📊 SNMP Configuration</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <InfoRow label="SNMP Version" value={olt.snmp_version || 'v2c'} />
                    <InfoRow label="SNMP UDP Port" value={olt.snmp_udp || olt.snmp_port || 161} />
                    <InfoRow label="SNMP Read-only Community" value={<MaskedField value={olt.snmp_community} />} />
                    <InfoRow label="SNMP Read-write Community" value={<MaskedField value={olt.snmp_rw_community} />} />
                    <InfoRow label="SNMP Trap Listener" value={olt.snmp_trap_listener || '—'} />
                  </tbody>
                </table>
              </div>

              {/* Hardware & Software */}
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ background: '#4a148c', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>⚙️ Hardware &amp; Software</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <InfoRow label="OLT Model / Hardware Version" value={modelDetected || olt.hw_version || olt.model} />
                    <InfoRow label="OLT Software Version" value={swVersion} />
                    <InfoRow label="Vendor" value={vendor || '—'} />
                    <InfoRow label="Supported PON Types" value={snmp?.pon_types?.join(', ') || '—'} />
                    <InfoRow label="System Description" value={sysDescr} />
                    <InfoRow label="Uptime" value={uptime} />
                  </tbody>
                </table>
              </div>

              {/* TR-069 & NTP */}
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ background: '#e65100', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>🌐 TR-069 &amp; NTP</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <InfoRow label="TR-069 ACS URL" value={olt.acs_url} mono />
                    <InfoRow label="TR-069 Profile" value={olt.tr069_profile || '—'} />
                    <InfoRow label="Default ONU TR-069 Interface" value={olt.tr069_interface || '—'} />
                    <InfoRow label="NTP Servers" value={olt.ntp_servers || '—'} />
                    <InfoRow label="Sync Interval" value={olt.sync_interval ? `${olt.sync_interval} min` : '10 min'} />
                    <InfoRow label="Last Sync" value={olt.last_sync ? new Date(olt.last_sync).toLocaleString() : '—'} />
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: Hardware visualization + stats */}
            <div style={{ width: 280, flexShrink: 0 }}>
              <OLTHardwareImage model={modelDetected || olt.model} vendor={vendor} cards={cards} uplinks={hardwareUplinks} loading={hardwareLoading} notes={hardwareNotes} />
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 16, marginTop: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: '#1565C0' }}>📈 Live Stats</div>
                {[['⏱ Uptime', uptime], ['💨 Fan Status', snmp?.fan_status || 'Unknown'], ['⚡ Power Usage', snmp?.power_usage || 'Unknown'], ['📡 SNMP Reachable', snmp?.reachable ? '✅ Yes' : snmpLoading ? '⏳ Polling...' : '❌ No']].map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <span style={{ fontWeight: 600 }}>{val}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 16, marginTop: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: '#1565C0' }}>ℹ️ Quick Info</div>
                {[['OLT ID', olt.id], ['Created', olt.created_at ? new Date(olt.created_at).toLocaleDateString() : '—'], ['Model', modelDetected || olt.model || '—']].map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <span style={{ fontWeight: 600 }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cards' && <OLTCardsTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'pon' && <PONPortsTab oltId={oltId} api={api} showToast={showToast} cards={cards} />}
        {activeTab === 'uplink' && <UplinkTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'vlans' && <VLANsTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'ip-pools' && <ONUIPPoolsTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'acls' && <RemoteACLsTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'profiles' && <ProfilesTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'voip' && <VoIPProfilesTab oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'settings' && <OLTSettingsTab olt={olt} oltId={oltId} api={api} showToast={showToast} />}
        {activeTab === 'advanced' && <AdvancedTab oltId={oltId} api={api} showToast={showToast} />}
      </div>
    </div>
  );
}
